import { useCallback, useMemo, useRef, useState } from 'react'
import type {
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
} from 'react-native'
import { StyleSheet, View } from 'react-native'
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg'

// Height of the full-width edge gradient.
const SCROLL_FADE_HEIGHT = 42

// Keeps sub-pixel offsets from flipping a fade on/off at the very top/bottom.
const SCROLL_EPSILON = 1

/**
 * Fades a scrolling list into the modal surface at one edge. Uses
 * react-native-svg since RN has no CSS gradient. Pair with
 * `useScrollEdgeFades` to decide when each edge shows.
 */
export function ScrollFade({
  edge,
  color,
  id,
}: {
  edge: 'top' | 'bottom'
  color: string
  /** Gradient id prefix — must be unique per screen on one mounted tree. */
  id: string
}) {
  const gradientId = `${id}-fade-${edge}`
  const topOpaque = edge === 'top'
  return (
    <View
      pointerEvents="none"
      style={[styles.fade, edge === 'top' ? { top: 0 } : { bottom: 0 }]}
    >
      <Svg width="100%" height="100%">
        <Defs>
          <LinearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <Stop
              offset="0"
              stopColor={color}
              stopOpacity={topOpaque ? 1 : 0}
            />
            <Stop
              offset="1"
              stopColor={color}
              stopOpacity={topOpaque ? 0 : 1}
            />
          </LinearGradient>
        </Defs>
        <Rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill={`url(#${gradientId})`}
        />
      </Svg>
    </View>
  )
}

/**
 * Tracks which edges of a `ScrollView` still have content beyond them, for
 * `ScrollFade`. Spread `scrollProps` onto the `ScrollView`.
 *
 * A caller wanting the scroll position too passes its own handler in rather
 * than merging one over `scrollProps.onScroll`: the merged object would be
 * new on every render, and gorhom hands `onScroll` to reanimated as a
 * dependency — a fresh one tears the native scroll handler down and registers
 * it again, mid-scroll, on the thread doing the scrolling.
 */
export function useScrollEdgeFades(
  onScrolled?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void,
) {
  const [showTopFade, setShowTopFade] = useState(false)
  const [showBottomFade, setShowBottomFade] = useState(false)
  const metrics = useRef({ scrollY: 0, viewportH: 0, contentH: 0 })

  // Reached through a ref so the handlers below stay the same functions for
  // the life of the list.
  const scrolled = useRef(onScrolled)
  scrolled.current = onScrolled

  const recomputeFades = useCallback(() => {
    const { scrollY, viewportH, contentH } = metrics.current
    const overflows = contentH > viewportH + SCROLL_EPSILON
    setShowTopFade(overflows && scrollY > SCROLL_EPSILON)
    setShowBottomFade(
      overflows && scrollY + viewportH < contentH - SCROLL_EPSILON,
    )
  }, [])

  const onScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      metrics.current.scrollY = e.nativeEvent.contentOffset.y
      recomputeFades()
      scrolled.current?.(e)
    },
    [recomputeFades],
  )
  const onLayout = useCallback(
    (e: LayoutChangeEvent) => {
      metrics.current.viewportH = e.nativeEvent.layout.height
      recomputeFades()
    },
    [recomputeFades],
  )
  const onContentSizeChange = useCallback(
    (_w: number, h: number) => {
      metrics.current.contentH = h
      // A shorter replacement list can clamp the native offset without firing
      // onScroll, so re-clamp scrollY to the new max or the bottom fade may hide
      // while the list still overflows.
      metrics.current.scrollY = Math.min(
        metrics.current.scrollY,
        Math.max(0, h - metrics.current.viewportH),
      )
      recomputeFades()
    },
    [recomputeFades],
  )

  const scrollProps = useMemo(
    () => ({ onScroll, onLayout, onContentSizeChange }),
    [onScroll, onLayout, onContentSizeChange],
  )

  return { showTopFade, showBottomFade, scrollProps }
}

const styles = StyleSheet.create({
  fade: {
    position: 'absolute',
    left: 0,
    right: 0,
    height: SCROLL_FADE_HEIGHT,
  },
})
